import { Capacitor, registerPlugin } from '@capacitor/core';
import { HomeRemindersElement } from 'features/Reminders/types';

// Same native plugin sharedPreferences.ts talks to (registered again here,
// which Capacitor's registerPlugin supports - it just hands back another
// proxy onto the same native-side plugin), because that file's exported
// helpers also write into Capacitor's LOCAL device Preferences store as
// part of their own contract, which this snapshot has no reason to do -
// it only ever needs to reach the App Group. Only `set`/`remove` are
// declared; `get` is omitted because nothing here ever reads the
// snapshot back - the widget process does that.
interface SharedPreferencesPlugin {
  set(options: { key: string; value: string }): Promise<void>;
  remove(options: { key: string }): Promise<void>;
}

let SharedPreferences: SharedPreferencesPlugin | null = null;

if (Capacitor.getPlatform() === 'ios') {
  SharedPreferences = registerPlugin<SharedPreferencesPlugin>('SharedPreferences');
}

// Bare key - the native plugin (SharedPreferencesPlugin.swift) prefixes
// every key with "CapacitorStorage." itself before writing into the App
// Group, so the value actually stored under is "CapacitorStorage.widgetSnapshot".
// Passing the prefix from here would double it.
const WIDGET_SNAPSHOT_KEY = 'widgetSnapshot';

// The widget's Swift decoder (ios/App/DoqitWidget/WidgetSnapshot.swift) is
// written against this exact shape - do not change field names, nesting or
// the version number without updating it too.
//
// v2 (was v1): each item now carries the REMINDER's own id alongside the
// document id. Two reminders can legitimately share one document_id (the
// backend's Document::createMany and generateRepeatingReminders both emit
// several reminders per document), and the widget keys SwiftUI's
// `Identifiable`/`ForEach` on this id - duplicate ids in a ForEach are
// undefined behaviour. A widget build expecting v2 treats a leftover v1
// payload as malformed and shows the "open Doqit to refresh" nudge until
// the dashboard next writes, which is the correct degradation.
const SNAPSHOT_VERSION = 2;

// How many items the snapshot carries at most, and therefore how many rows
// the widget's largest family can ever show. Exported because the dashboard
// has to ask the API for this many (see homeRemindersLimit below) - before
// that, the endpoint could only ever return one row, so every widget size
// showed exactly one renewal no matter what this cap said.
const MAX_ITEMS = 5;

interface WidgetSnapshotItem {
  reminderId: number;
  documentId: number;
  name: string;
  reminderDate: string;
  // Nullable on purpose: `documents.renewal_date` is a nullable column
  // server-side, so this really can arrive as null however the shared
  // response type is declared. The Swift decoder declares it optional to
  // match - a single null must not fail the decode of the whole payload.
  renewalDate: string | null;
}

interface WidgetSnapshotPayload {
  v: number;
  writtenAt: number;
  more: number;
  items: WidgetSnapshotItem[];
}

// How many reminders the dashboard should ask /reminders/home for.
//
// `undefined` off iOS means the request is sent with NO `limit` parameter at
// all, so web and Android hit the endpoint's default of 1 and their traffic
// is byte-identical to before this existed. On iOS the dashboard asks for
// MAX_ITEMS so the snapshot can actually be filled; it still renders only
// reminder[0] itself, exactly as it always has.
export const homeRemindersLimit = (): number | undefined => (
  SharedPreferences ? MAX_ITEMS : undefined
);

// Defeats the same class of race `spotlight.ts`'s `clearGeneration` exists
// for, which this helper originally had no equivalent of. Two distinct
// interleavings have to be closed, and they need two different mechanisms:
//
// 1. CLEAR LANDS DURING A WRITE'S OWN AWAIT. The dashboard's write is issued,
//    then a logout/clear runs and completes, then the write's native `set`
//    finally lands on top of it - leaving a signed-out user's document names
//    in the App Group. `clearGeneration` is bumped SYNCHRONOUSLY as the first
//    statement of clearWidgetSnapshot (before its own native call even
//    starts, so it cannot itself be raced); writeWidgetSnapshot captures it
//    before awaiting and re-removes if it changed, rather than trusting what
//    it just wrote.
//
// 2. CLEAR LANDS STRICTLY BEFORE A LATE WRITE EVEN STARTS. This is the more
//    reachable one and a generation counter alone does NOT catch it: the
//    reminders request is already in flight when credentials are cleared, so
//    writeWidgetSnapshot is not entered until AFTER the clear has finished,
//    and its captured generation therefore matches. Most reachable via token
//    rotation - one request 401s on a stale bearer and triggers
//    clearCredentials through the RTK error handler, while a concurrent
//    request carrying the newer bearer succeeds and its `.then` writes the
//    snapshot back. `writesSuspended` is a latch closed by the same
//    synchronous first statements of clearWidgetSnapshot and only reopened by
//    an explicit sign-in (setCredentials calls resumeWidgetSnapshotWrites).
//
// Why a latch reset on setCredentials rather than an auth re-check inside
// writeWidgetSnapshot:
//   - authSlice.ts already imports THIS module (it calls clearWidgetSnapshot
//     from the clearCredentials reducer), so reading auth state from here
//     would make the two modules import each other. A circular import through
//     a redux-persist-backed slice is a real hazard, not a stylistic one.
//   - The alternative auth source is Capacitor Preferences, which is another
//     `await` - i.e. another window for exactly this race - whereas the latch
//     is read synchronously and cannot be raced at all.
//   - The reopen point is genuinely a sign-in and nothing else: token
//     ROTATION dispatches `refreshToken`, NOT `setCredentials` (see
//     authSlice.ts), so the rotation interleaving above can never reopen the
//     latch it just closed.
// The failure direction is also the safe one: if the latch ever stays shut
// when it should not, the widget shows nothing until the app is reopened,
// rather than showing a signed-out user's documents.
let clearGeneration = 0;
let writesSuspended = false;

// Called from authSlice's setCredentials reducer - see writesSuspended above
// for why this is the only thing that reopens the latch.
export const resumeWidgetSnapshotWrites = (): void => {
  writesSuspended = false;
};

const removeSnapshot = async (): Promise<void> => {
  if (!SharedPreferences) return;
  try {
    await SharedPreferences.remove({ key: WIDGET_SNAPSHOT_KEY });
  } catch (error) {
    console.error('Failed to clear widget snapshot:', error);
  }
};

// Writes the widget's home-screen snapshot into the App Group. iOS-gated
// (SharedPreferences stays null everywhere else, so this is a no-op on
// web and Android) and never throws to the caller - mirrors
// sharedPreferences.ts's discipline, because a widget snapshot failing to
// write must never interrupt the dashboard load it rides in on.
//
// Serialises ONLY the five fields the widget reads - reminderId, documentId,
// name, reminderDate, renewalDate - out of each HomeRemindersElement. Never
// the whole reminder or document object, so contents, categories and notes
// can never reach the App Group via this path. Callers may pass more
// than MAX_ITEMS items; this caps it, so callers can hand it the full
// response array without pre-slicing.
export const writeWidgetSnapshot = async (
  items: HomeRemindersElement[],
  more: number,
): Promise<void> => {
  if (!SharedPreferences) return;
  // Interleaving 2 above: a clear has already happened and nothing has signed
  // in since, so this response belongs to a session that is over.
  if (writesSuspended) return;

  // Interleaving 1 above: captured before the native call, compared after it.
  const generationBeforeWrite = clearGeneration;

  const payload: WidgetSnapshotPayload = {
    v: SNAPSHOT_VERSION,
    writtenAt: Date.now(),
    more,
    items: items.slice(0, MAX_ITEMS).map((item) => ({
      reminderId: item.id,
      documentId: item.document_id,
      name: item.document.name,
      reminderDate: item.reminder_date,
      renewalDate: item.document.renewal_date ?? null,
    })),
  };

  try {
    await SharedPreferences.set({ key: WIDGET_SNAPSHOT_KEY, value: JSON.stringify(payload) });
  } catch (error) {
    console.error('Failed to write widget snapshot:', error);
    return;
  }

  // A clearWidgetSnapshot() call landed while the set() above was in flight,
  // so what has just been written to the App Group is a session's worth of
  // document names that is no longer supposed to exist. Re-remove rather than
  // trusting the write - the two native calls are not guaranteed to resolve
  // in the order they were issued.
  if (clearGeneration !== generationBeforeWrite) {
    await removeSnapshot();
  }
};

// Clears the widget's snapshot. Called from four sites - the clearCredentials
// reducer, the PURGE extraReducer, MenuBar's onLogoutHandler, and
// deleteUserAction's onQueryStarted - each covering a credential-loss path
// the others don't reach; see each call site's own comment for why. A
// signed-out or wiped account must never leave its renewals showing on the
// home screen. No-op off iOS; never throws.
export const clearWidgetSnapshot = async (): Promise<void> => {
  // Both bumped synchronously, as the first statements here, before the
  // native call even starts - see the clearGeneration/writesSuspended comment
  // above for the two interleavings this closes. Deliberately ahead of the
  // iOS gate too, so the guard state can never depend on platform detection.
  clearGeneration += 1;
  writesSuspended = true;
  await removeSnapshot();
};
